<template>
  <article
    class="relative flex grow flex-col bg-white px-6 py-5 pb-8 ring-1 ring-gray-tertiary"
  >
    <div class="aspect-h-9 aspect-w-16 relative mb-4">
      <NuxtLink :to="ctaLink" class="flex" @click="trackArticleClick(title)">
        <span class="sr-only">{{ srLinkLabel }}</span>
        <NuxtLazyImage :source="image.src" :alt="image.alt" cover />
      </NuxtLink>
    </div>
    <div class="mb-4 flex justify-center gap-4 text-base">
      <TagList
        :tags="tags"
        :selected-tag-uuids="selectedTagUuids"
        placement="big teaser"
        class="!text-gray-secondary"
      />
    </div>
    <NuxtLink :to="ctaLink" class="flex" @click="trackArticleClick(title)">
      <Headline
        size="xl"
        :tag="tagType ? 'span' : 'h2'"
        class="my-4 !block w-full justify-center whitespace-pre-line break-words text-center !font-bold tracking-wider"
        :is-uppercase="false"
        >{{ title }}</Headline
      >
    </NuxtLink>

    <div class="flex grow flex-col justify-between">
      <p class="mb-8 text-center tracking-wider">
        {{ description }}
      </p>
      <div class="mb-2 flex justify-center">
        <NuxtLink :to="ctaLink" class="flex" @click="trackArticleClick(title)">
          <Button
            :ignore-capitalisation="true"
            :stop-propagation="false"
            :prevent-default="false"
            class="!px-10 !py-2"
          >
            {{ $t('blog.open_article') }}
          </Button>
        </NuxtLink>
      </div>
    </div>
  </article>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { useNuxtApp } from '#app/nuxt'
import useRouteHelpers from '#layers/base/composables/useRouteHelpers'
import NuxtLazyImage from '~/components/images/NuxtLazyImage.vue'
import Headline from '#layers/base/components/ui/headlines/Headline.vue'
import Button from '~/components/ui/buttons/Button.vue'
import TagList from '~/components/blog/TagList.vue'
import useTrackingEvents from '~/composables/tracking/useTrackingEvents'
import type {
  SbBlogArticleV2,
  SbText,
  StoryblokStory,
} from '~/cms/storyblok/types/storyblok.d'
import { getCmsImage } from '~/storyblok/utils/cmsImages'

const MAX_LENGTH_OF_DESCRIPTION = 200

const { t } = useI18n()

type BlogTeaserProps = {
  blok: StoryblokStory<SbBlogArticleV2>
  selectedTagUuids?: (string | null)[]
  tagType?: boolean
}

const props = withDefaults(defineProps<BlogTeaserProps>(), {
  selectedTagUuids: () => [],
  tagType: false,
})

const { toLocalePath } = useRouteHelpers()
const { $device } = useNuxtApp()
const { trackBlogArticle } = useTrackingEvents()

const content = computed(() => props.blok.content)

const getImage = (content?: SbBlogArticleV2) => {
  let image = getCmsImage(content?.teaser_image?.[0], $device)
  if (!image.src) {
    image = getCmsImage(content?.image?.[0], $device)
  }
  return image
}

const truncate = (text: string = '') => {
  if (text.length > MAX_LENGTH_OF_DESCRIPTION) {
    const truncated = text.slice(0, MAX_LENGTH_OF_DESCRIPTION)
    const lastSpaceIndex = truncated.lastIndexOf(' ')
    return truncated.slice(0, lastSpaceIndex).concat('...')
  }
  return text
}

const getFallbackText = (blogArticle?: SbBlogArticleV2) => {
  const textComponent = blogArticle?.content?.find(
    (item: any) => item.component === 'CmsText',
  )
  return (textComponent as SbText)?.body?.content?.[0]?.content?.[0]?.text
}

const trackArticleClick = (title: string) => {
  trackBlogArticle({
    article_title: title,
    teaser_type: 'big teaser',
    blog_section: 'main list',
  })
}

const title = computed(
  () => content.value?.teaser_title || content.value?.title || '',
)

const image = computed(() => getImage(content.value))
const description = computed(() =>
  truncate(content.value?.teaser_description ?? getFallbackText(content.value)),
)
const ctaLink = computed(() => toLocalePath(props.blok.full_slug))
const tags = computed(
  () => content.value?.tags?.filter((tag) => typeof tag !== 'string') ?? [],
)
const srLinkLabel = computed(() => t('blog.open_article') + ' ' + title?.value)
</script>
